import React from "react";

import {
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  SquarePlay,
} from "lucide-react";

import { ACCENT, Bubble, Legend } from "./Chrome";
import BriefingMatrixSkeleton from "../../../Container/WatchlistSummary/BriefingMatrixSkeleton";
import {
  MATRIX_ACCOUNT_REVEAL_INTERVAL_MS,
  MetricStaggerWrap,
} from "../../../Container/WatchlistSummary/WatchlistSummaryMatrix";
import { useAppSelector } from "../../../Store/Hooks";
import type { XFactorBriefingDetail } from "../../../Types/XFactorBriefing";
import type { XFactorPortfolioAccount } from "../../../Types/XFactorViewsRendering";
import { DEFAULT_HEALTH_SCORE_THRESHOLDS } from "../../../Utils/HealthThresholds";
import {
  BAND_FILL,
  SECTION_COLUMN_LABEL,
  bandOf,
  presentSectionIds,
  renderRow,
  sortRows,
} from "../lib/derive";

/**
 * With "Show metrics" on, xfactor expands every section column into three
 * sub-columns. It is a column expander, not a "print the number" toggle.
 */
const SUB_COLUMNS: Array<{
  key: "risk" | "opportunity" | "confidence";
  label: string;
}> = [
  { key: "risk", label: "Risk" },
  { key: "opportunity", label: "Opportunity" },
  { key: "confidence", label: "Confidence" },
];

/**
 * Hairline behind each row's bubbles: from the first bubble centre to the last.
 * Painted as cell backgrounds so it never steals clicks.
 */
const RAIL = "#dbe4ee";
/**
 * Sticky Account column width — shared by header, cells, and brief inset.
 * Exported with railStyle so the KPI matrix (XfactorKpiMatrix) draws the
 * identical grid instead of an approximation of it.
 */
export const ACCOUNT_COL_PX = 200;
/**
 * Bubble column width — keeps Account Health density aligned with Business
 * Health at the shared `min-w-[1180px]` budget (11 BH bubble columns).
 * Without a cap, AH's fewer categories stretch `w-full` and open wide gaps.
 */
export const BUBBLE_COL_PX = 82;
/** Action column footprint (Situation Card + Full Briefing icons). */
export const ACTION_COL_PX = 88;
const GENERATING_BRIEFING_MESSAGE_ONE =
  "Business Health briefing for this account is being generated….check back in a few minutes.";
const GENERATING_BRIEFING_MESSAGE_MANY =
  "Business Health briefings are being generated for these accounts….check back in a few minutes.";
const GENERATING_BRIEFING_TOOLTIP =
  "This account was recently added - it typically takes 5-10 minutes to generate the Business Health briefing.";

export const railStyle = (
  index: number,
  count: number
): React.CSSProperties => {
  if (count < 2) return {};
  const start = index === 0 ? "50%" : "0%";
  const end = index === count - 1 ? "50%" : "100%";
  return {
    backgroundImage: `linear-gradient(to right, transparent ${start}, ${RAIL} ${start}, ${RAIL} ${end}, transparent ${end})`,
    backgroundSize: "100% 1px",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  };
};

/**
 * The accounts x sections matrix.
 *
 * Colour is section health, diameter is section relevance — xfactor's stated
 * convention. An absent section renders as a dashed outline rather than a zero,
 * because "not generated" and "scored zero" are different facts.
 *
 * Lives on its own because it renders in two places: the Bubble modal inside
 * the ExecBridge flow, and the Temp Public Data tab under Always On
 * Intelligence. Sharing the component is what keeps them from drifting.
 */
const BubbleMatrix: React.FC<{
  accounts: XFactorPortfolioAccount[];
  payloads: Record<string, { data: XFactorBriefingDetail | null }>;
  loading: boolean;
  onOpenBubble: (digestId: string, sectionId: string) => void;
  showMetrics: boolean;
  showLegend?: boolean;
  /**
   * The digest date under each account name. On its own page the date is the
   * only freshness signal there is; embedded in a tab that already states the
   * briefing date once, it is repetition.
   */
  showAccountMeta?: boolean;
  /**
   * When supplied, the matrix grows an Action column matching the portfolio
   * table's. xfactor's own bubble chart has no such column, so the modal that
   * mirrors it leaves this off.
   */
  onOpenAccount?: (account: XFactorPortfolioAccount) => void;
  /** Row-end Full Briefing control, beside the Action button. */
  onOpenFullBriefing?: (account: XFactorPortfolioAccount) => void;
  /**
   * The expandable one-line account summary under each row, as EB's existing
   * Public Data chart draws it. Off inside the Bubble modal, which mirrors
   * xfactor's own chart and has no such row.
   */
  showSummary?: boolean;
  /** Account Health–style compare selection (EB customer ids). */
  selectedAccountIds?: string[];
  onToggleAccountSelection?: (accountId: string) => void;
  /**
   * Public Data–style load: pulsing skeleton dots, then rows appear one by
   * one. Off in the portfolio bubble modal so that chart stays instant.
   */
  enableRowReveal?: boolean;
  /** Fired once when the progressive row reveal finishes (or is skipped). */
  onRevealComplete?: () => void;
  /**
   * When true, generating rows stay together at the bottom. Name order inside
   * each group is unchanged — this is grouping, not a sort control.
   */
  groupGeneratingLast?: boolean;
}> = ({
  accounts,
  payloads,
  loading,
  onOpenBubble,
  showMetrics,
  showLegend = true,
  showAccountMeta = true,
  onOpenAccount,
  onOpenFullBriefing,
  showSummary = false,
  selectedAccountIds = [],
  onToggleAccountSelection,
  enableRowReveal = false,
  onRevealComplete,
  groupGeneratingLast = false,
}) => {
  const bhThresholds = useAppSelector(
    state =>
      state.vendorAdmin.healthScoreThresholds?.business_health ??
      DEFAULT_HEALTH_SCORE_THRESHOLDS.business_health
  );
  const accountsById = React.useMemo(
    () => new Map(accounts.map(a => [a.digest_id, a])),
    [accounts]
  );
  const [sort, setSort] = React.useState<{
    key: string;
    dir: "asc" | "desc";
  }>({ key: "account", dir: "asc" });

  /**
   * Only rows the reader has actually toggled. Everything else falls back to
   * "first row open, rest closed" — the existing chart's default — which then
   * follows the sort rather than pinning to whichever account happened to load
   * first.
   */
  const [summaryOverrides, setSummaryOverrides] = React.useState<
    Record<string, boolean>
  >({});
  const isSummaryOpen = (digestId: string, index: number) =>
    summaryOverrides[digestId] ?? index === 0;
  const toggleBrief = (digestId: string, currentlyOpen: boolean) =>
    setSummaryOverrides(s => ({
      ...s,
      [digestId]: !currentlyOpen,
    }));

  // Always every canonical section, in xfactor's display order, plus any
  // unexpected id some account carries. Columns therefore do not appear and
  // disappear as accounts load, and a section missing from one account renders
  // hollow in that row rather than shifting the grid.
  const sectionIds = React.useMemo(() => {
    const canonical = presentSectionIds(null);
    const extra = new Set<string>();
    accounts.forEach(a => {
      presentSectionIds(payloads[a.digest_id]?.data?.data).forEach(id => {
        if (!canonical.includes(id)) extra.add(id);
      });
    });
    return [...canonical, ...Array.from(extra).sort()];
  }, [accounts, payloads]);

  const rows = React.useMemo(() => {
    const built = accounts.map(a =>
      renderRow(a, payloads[a.digest_id]?.data?.data, sectionIds)
    );
    const selectedIds = new Set(
      selectedAccountIds.map(id => String(id || "").trim()).filter(Boolean)
    );
    const compareSelected = (
      left: (typeof built)[number],
      right: (typeof built)[number]
    ) => {
      if (selectedIds.size === 0) return 0;
      const leftSelected = selectedIds.has(String(left.accountId || ""));
      const rightSelected = selectedIds.has(String(right.accountId || ""));
      if (leftSelected === rightSelected) return 0;
      return leftSelected ? -1 : 1;
    };

    if (sort.key === "account") {
      return [...built].sort((a, b) => {
        const selectedCompare = compareSelected(a, b);
        if (selectedCompare !== 0) return selectedCompare;
        if (groupGeneratingLast) {
          const aGenerating = Boolean(a.overall.generating);
          const bGenerating = Boolean(b.overall.generating);
          if (aGenerating !== bGenerating) return aGenerating ? 1 : -1;
        }
        const cmp = a.accountName.localeCompare(b.accountName, undefined, {
          sensitivity: "base",
        });
        return sort.dir === "asc" ? cmp : -cmp;
      });
    }

    const scoreSorted = sortRows(
      built,
      row =>
        sort.key === "summary"
          ? row.overall.score
          : (row.bubbles.find(b => b.sectionId === sort.key)?.score ?? null),
      sort.dir
    );
    if (selectedIds.size === 0) return scoreSorted;
    return [...scoreSorted].sort(compareSelected);
  }, [
    accounts,
    payloads,
    sectionIds,
    sort,
    selectedAccountIds,
    groupGeneratingLast,
  ]);

  const selectionEnabled = typeof onToggleAccountSelection === "function";

  const prefersReducedMotion = React.useMemo(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  /** Stable account set from the API — not sort/selection order. */
  const accountSetKey = React.useMemo(
    () =>
      accounts
        .map(a => a.digest_id)
        .slice()
        .sort()
        .join(","),
    [accounts]
  );

  const [visibleRowCount, setVisibleRowCount] = React.useState(0);
  const [staggerActive, setStaggerActive] = React.useState(enableRowReveal);
  const revealedForKeyRef = React.useRef<string | null>(null);
  const revealCompleteNotifiedRef = React.useRef(false);
  const onRevealCompleteRef = React.useRef(onRevealComplete);
  onRevealCompleteRef.current = onRevealComplete;

  const notifyRevealComplete = React.useCallback(() => {
    if (revealCompleteNotifiedRef.current) return;
    revealCompleteNotifiedRef.current = true;
    onRevealCompleteRef.current?.();
  }, []);

  React.useEffect(() => {
    if (!enableRowReveal || prefersReducedMotion) {
      setStaggerActive(false);
      setVisibleRowCount(rows.length);
      revealedForKeyRef.current = accountSetKey || null;
      if (!loading && rows.length) notifyRevealComplete();
      return;
    }

    // After the first reveal for this mount, never restart for selection /
    // sort / page-slice account-set changes.
    if (revealCompleteNotifiedRef.current) {
      setStaggerActive(false);
      setVisibleRowCount(rows.length);
      return;
    }

    if (loading || !rows.length) {
      if (revealedForKeyRef.current !== accountSetKey) {
        setStaggerActive(true);
        setVisibleRowCount(0);
      }
      return;
    }

    if (revealedForKeyRef.current === accountSetKey) {
      setStaggerActive(false);
      setVisibleRowCount(rows.length);
      notifyRevealComplete();
      return;
    }

    setStaggerActive(true);
    setVisibleRowCount(1);
    const interval = window.setInterval(() => {
      setVisibleRowCount(prev => {
        if (prev >= rows.length) {
          window.clearInterval(interval);
          revealedForKeyRef.current = accountSetKey;
          setStaggerActive(false);
          notifyRevealComplete();
          return prev;
        }
        return prev + 1;
      });
    }, MATRIX_ACCOUNT_REVEAL_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [
    loading,
    enableRowReveal,
    prefersReducedMotion,
    accountSetKey,
    rows.length,
    notifyRevealComplete,
  ]);

  const displayedRows =
    enableRowReveal && staggerActive && !prefersReducedMotion
      ? rows.slice(0, Math.max(visibleRowCount, 0))
      : rows;

  /** Deduplicate the generating notice — one line for the whole generating group. */
  const generatingCount = React.useMemo(
    () => displayedRows.filter(row => Boolean(row.overall.generating)).length,
    [displayedRows]
  );
  const firstGeneratingDigestId = React.useMemo(
    () =>
      displayedRows.find(row => Boolean(row.overall.generating))?.digestId ??
      null,
    [displayedRows]
  );

  const toggleSort = (key: string) =>
    setSort(s =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "account" ? "asc" : "desc" }
    );

  const columns = [
    { id: "summary", label: SECTION_COLUMN_LABEL.summary },
    ...sectionIds.map(id => ({
      id,
      label: SECTION_COLUMN_LABEL[id] || id,
    })),
  ];

  /** Account column + every bubble column (times its sub-columns) + Action. */
  const totalColumns =
    1 +
    columns.length * (showMetrics ? 1 + SUB_COLUMNS.length : 1) +
    (onOpenAccount || onOpenFullBriefing ? 1 : 0);

  return (
    <>
      {showLegend ? (
        <div className="mb-4 rounded-lg bg-gray-50 px-4 py-3">
          <Legend />
        </div>
      ) : null}
      {loading ? (
        <BriefingMatrixSkeleton
          sections={columns
            .filter(c => c.id !== "summary")
            .map(c => ({ id: c.id, name: c.label }))}
          caption="Generating intelligence briefings for public data."
          ariaLabel="Generating intelligence briefings for public data."
          showActions={Boolean(onOpenAccount || onOpenFullBriefing)}
        />
      ) : (
        <div
          className={`watchlist-summary-matrix watchlist-summary-matrix--embed${
            staggerActive && enableRowReveal ? "" : " matrix-stagger-suppressed"
          }`}
        >
          <table className="w-full min-w-[1180px] border-separate border-spacing-0 text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th
                  className="sticky top-0 left-0 z-40 bg-[#f1f5f9] px-3 py-2 text-center align-bottom shadow-[0_1px_0_0_#e2e8f0]"
                  style={{
                    width: ACCOUNT_COL_PX,
                    minWidth: ACCOUNT_COL_PX,
                    maxWidth: ACCOUNT_COL_PX,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => toggleSort("account")}
                    className="mx-auto block w-full text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-900"
                  >
                    <span className="inline-flex items-center justify-center gap-1">
                      Account <ArrowUpDown size={11} aria-hidden />
                    </span>
                    <span className="block font-normal normal-case tracking-normal text-gray-400">
                      ({rows.length} total)
                    </span>
                  </button>
                </th>
                {columns.map(c => (
                  <React.Fragment key={c.id}>
                    <th className="sticky top-0 z-30 border-l border-gray-200 bg-[#f1f5f9] px-2 py-2.5 text-center align-middle shadow-[0_1px_0_0_#e2e8f0]">
                      <button
                        type="button"
                        onClick={() => toggleSort(c.id)}
                        title={`Sort by ${c.label}`}
                        className="mx-auto block w-full text-center text-[11px] font-semibold leading-tight text-gray-600 hover:text-gray-900"
                      >
                        {c.label}
                        {sort.key === c.id
                          ? sort.dir === "asc"
                            ? " ↑"
                            : " ↓"
                          : ""}
                      </button>
                    </th>
                    {showMetrics
                      ? SUB_COLUMNS.map(sub => (
                          <th
                            key={`${c.id}-${sub.key}`}
                            className="sticky top-0 z-30 bg-[#f1f5f9] px-1 py-2 align-bottom shadow-[0_1px_0_0_#e2e8f0]"
                          >
                            <span className="mx-auto block text-center text-[10px] leading-tight text-gray-400">
                              {sub.label}
                            </span>
                          </th>
                        ))
                      : null}
                  </React.Fragment>
                ))}
                {onOpenAccount || onOpenFullBriefing ? (
                  <th className="sticky top-0 z-30 border-l border-gray-200 bg-[#f1f5f9] px-3 py-2.5 align-middle shadow-[0_1px_0_0_#e2e8f0]">
                    <span className="block text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      Action
                    </span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {displayedRows.map((row, rowIndex) => {
                const isGenerating = Boolean(row.overall.generating);
                const summary =
                  showSummary && !isGenerating
                    ? row.overall.synopsis?.trim()
                    : null;
                const canToggleGenerating = showSummary && isGenerating;
                const generatingOpen =
                  canToggleGenerating &&
                  (summaryOverrides[row.digestId] ??
                    row.digestId === firstGeneratingDigestId);
                const showGeneratingLine = Boolean(generatingOpen);
                const summaryOpen =
                  !!summary && isSummaryOpen(row.digestId, rowIndex);
                const briefOpen = showGeneratingLine || summaryOpen;
                const animateReveal = staggerActive && enableRowReveal;
                const generatingLineText =
                  row.digestId === firstGeneratingDigestId &&
                  generatingCount > 1
                    ? GENERATING_BRIEFING_MESSAGE_MANY
                    : GENERATING_BRIEFING_MESSAGE_ONE;
                const briefLine = showGeneratingLine
                  ? generatingLineText
                  : summary;
                const showBriefLine =
                  showGeneratingLine || (!!summaryOpen && !!summary);
                return (
                  <React.Fragment key={row.digestId}>
                    <tr className="border-t border-gray-100 hover:bg-gray-50/60">
                      <td
                        className={`sticky left-0 z-10 border-r border-gray-100 bg-white px-3 py-2 align-middle${
                          showBriefLine
                            ? " text-center border-b border-slate-200"
                            : ""
                        }`}
                        rowSpan={showBriefLine ? 2 : undefined}
                        style={{
                          width: ACCOUNT_COL_PX,
                          minWidth: ACCOUNT_COL_PX,
                          maxWidth: ACCOUNT_COL_PX,
                        }}
                      >
                        {(() => {
                          const selectControl =
                            selectionEnabled && row.accountId ? (
                              <label
                                className="relative inline-flex shrink-0 cursor-pointer items-center justify-center"
                                onClick={event => event.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  aria-label={`Select ${row.accountName} for comparison`}
                                  checked={selectedAccountIds.includes(
                                    row.accountId
                                  )}
                                  onChange={() =>
                                    onToggleAccountSelection?.(row.accountId)
                                  }
                                  className="peer sr-only"
                                />
                                <span className="h-4 w-4 rounded-[4px] border border-slate-300 bg-white shadow-sm transition-colors peer-hover:border-slate-400 peer-focus-visible:ring-2 peer-focus-visible:ring-violet-200 peer-checked:border-violet-600 peer-checked:bg-violet-600" />
                                <svg
                                  viewBox="0 0 16 16"
                                  className="pointer-events-none absolute h-2.5 w-2.5 text-white opacity-0 transition-opacity peer-checked:opacity-100"
                                  aria-hidden="true"
                                >
                                  <path
                                    d="M3.5 8.5L6.3 11.3L12.5 5.1"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </label>
                            ) : null;
                          const briefToggle =
                            summary || canToggleGenerating ? (
                              <button
                                type="button"
                                onClick={() =>
                                  toggleBrief(row.digestId, briefOpen)
                                }
                                aria-expanded={briefOpen}
                                aria-controls={`bubble-summary-${row.digestId}`}
                                title={
                                  briefOpen
                                    ? canToggleGenerating
                                      ? "Hide generating status"
                                      : "Hide summary"
                                    : canToggleGenerating
                                      ? "Show generating status"
                                      : "Show summary"
                                }
                                aria-label={
                                  briefOpen
                                    ? canToggleGenerating
                                      ? `Hide ${row.accountName} generating status`
                                      : `Hide ${row.accountName} summary`
                                    : canToggleGenerating
                                      ? `Show ${row.accountName} generating status`
                                      : `Show ${row.accountName} summary`
                                }
                                className="shrink-0 text-gray-400 hover:text-gray-700"
                              >
                                {briefOpen ? (
                                  <ChevronDown size={14} aria-hidden />
                                ) : (
                                  <ChevronRight size={14} aria-hidden />
                                )}
                              </button>
                            ) : null;

                          if (showBriefLine) {
                            // Merged ACCOUNT cell (rowSpan 2): equal side gutters
                            // keep controls clear of the centered name.
                            return (
                              <div className="flex w-full min-w-0 items-center">
                                <div className="flex w-10 shrink-0 items-center justify-start gap-1.5">
                                  {selectControl}
                                  {briefToggle}
                                </div>
                                <div className="min-w-0 flex-1 px-1 text-center">
                                  <div
                                    className="truncate text-center font-medium text-gray-900"
                                    title={row.accountName}
                                  >
                                    {row.accountName}
                                  </div>
                                  {showAccountMeta && row.accountMeta ? (
                                    <div className="truncate text-center text-[11px] text-gray-400">
                                      {row.accountMeta}
                                    </div>
                                  ) : null}
                                </div>
                                <div className="w-10 shrink-0" aria-hidden />
                              </div>
                            );
                          }

                          return (
                            <div className="flex items-center justify-start gap-1.5">
                              {selectControl}
                              {briefToggle}
                              <div className="min-w-0 flex-1">
                                <div
                                  className="block truncate text-left font-medium text-gray-900"
                                  title={row.accountName}
                                >
                                  {row.accountName}
                                </div>
                                {showAccountMeta && row.accountMeta ? (
                                  <div className="truncate text-left text-[11px] text-gray-400">
                                    {row.accountMeta}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                      {[row.overall, ...row.bubbles].map((bubble, i, all) => (
                        <React.Fragment key={bubble.sectionId}>
                          <td
                            className="px-1 py-1 text-center align-middle"
                            style={
                              showMetrics ? undefined : railStyle(i, all.length)
                            }
                          >
                            {animateReveal ? (
                              <MetricStaggerWrap staggerIndex={i}>
                                <Bubble
                                  bubble={bubble}
                                  waveIndex={i}
                                  onClick={() =>
                                    onOpenBubble(row.digestId, bubble.sectionId)
                                  }
                                />
                              </MetricStaggerWrap>
                            ) : (
                              <Bubble
                                bubble={bubble}
                                waveIndex={i}
                                onClick={() =>
                                  onOpenBubble(row.digestId, bubble.sectionId)
                                }
                              />
                            )}
                          </td>
                          {showMetrics
                            ? SUB_COLUMNS.map(sub => {
                                const value = bubble[sub.key];
                                return (
                                  <td
                                    key={`${bubble.sectionId}-${sub.key}`}
                                    className="px-1 py-1 text-center"
                                    title={`${sub.label}: ${
                                      value == null
                                        ? "not scored"
                                        : Math.round(value)
                                    }`}
                                  >
                                    <span
                                      className="mx-auto block h-2.5 w-2.5 rounded-full"
                                      style={{
                                        backgroundColor:
                                          value == null
                                            ? "transparent"
                                            : BAND_FILL[
                                                bandOf(value, bhThresholds)
                                              ],
                                        border:
                                          value == null
                                            ? "1.5px dashed #cbd5e1"
                                            : "none",
                                      }}
                                    />
                                  </td>
                                );
                              })
                            : null}
                        </React.Fragment>
                      ))}
                      {onOpenAccount || onOpenFullBriefing ? (
                        <td className="whitespace-nowrap px-3 py-1 text-center align-middle">
                          {onOpenAccount ? (
                            <button
                              type="button"
                              onClick={() => {
                                const account = accountsById.get(row.digestId);
                                if (account) onOpenAccount(account);
                              }}
                              title="Situation Card"
                              aria-label={`Open ${row.accountName} situation card`}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-[#ad46ff]/10"
                              style={{ color: ACCENT }}
                            >
                              <SquarePlay size={22} />
                            </button>
                          ) : null}
                          {onOpenFullBriefing ? (
                            <button
                              type="button"
                              onClick={() => {
                                const account = accountsById.get(row.digestId);
                                if (account) onOpenFullBriefing(account);
                              }}
                              title="Full Briefing"
                              aria-label={`Open ${row.accountName} full briefing`}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-[#ad46ff]/10"
                              style={{ color: ACCENT }}
                            >
                              <ClipboardList size={20} />
                            </button>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                    {showBriefLine && briefLine ? (
                      <tr
                        id={`bubble-summary-${row.digestId}`}
                        className="table-row account-brief-row"
                        aria-label={
                          showGeneratingLine
                            ? `${row.accountName} briefing generating`
                            : `${row.accountName} briefing summary`
                        }
                      >
                        <td
                          colSpan={Math.max(1, totalColumns - 1)}
                          className="account-brief-line-cell"
                        >
                          <p
                            title={
                              showGeneratingLine
                                ? GENERATING_BRIEFING_TOOLTIP
                                : briefLine
                            }
                            className={`account-brief-line account-brief-line--full${
                              showGeneratingLine ? " cursor-help" : ""
                            }${
                              animateReveal && !showGeneratingLine
                                ? " ai-generating-text"
                                : ""
                            }`}
                          >
                            {briefLine}
                          </p>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default BubbleMatrix;
